import { captureException } from '@sentry/react';
import {
  applyIgniteInterceptors,
  type AxiosInstance,
  type AxiosRequestConfig,
  type BodyType,
  createCorrelationIdInterceptor,
  createIgniteAxiosClient,
  createIgniteAxiosClientInstance,
  type ErrorType,
} from '@alchemer/api';
import { useBoundStore } from '@/store';

/**
 * GlobalAxiosClient - Axios client for global services (e.g., impersonation)
 *
 * This client is used for API calls to global services that are NOT region-specific.
 * Unlike IgniteAxiosClient, this client uses a static global gateway URL
 * (VITE_API_GATEWAY_URL) instead of dynamically switching based on the user's selected region.
 *
 * Use this client for:
 * - Impersonation API calls (global service)
 * - Any other services that are deployed globally rather than per-region
 *
 * Do NOT use this client for:
 * - Dashboard API calls (use IgniteAxiosClient instead)
 * - Region-specific services
 */

const GLOBAL_GATEWAY_URL =
  (typeof import.meta !== 'undefined' ? import.meta.env?.VITE_API_GATEWAY_URL : undefined) ||
  process.env.VITE_API_GATEWAY_URL ||
  '';

if (!GLOBAL_GATEWAY_URL) {
  console.warn('[GlobalAxiosClient] VITE_API_GATEWAY_URL is not set. Global API calls may fail.');
}

// Create an accessor function that will retrieve the token from Auth0 - used by our interceptors
let authTokenAccessor: () => Promise<string | null> | null = () => null;
let logoutActionHandler: (() => Promise<void>) | null = null;

/**
 * Set the accessor function to retrieve the auth token
 * @param accessor
 */
export const setAuthTokenAccessor = (accessor: () => Promise<string | null> | null) => {
  authTokenAccessor = accessor;
};

/**
 * Set the logout action to be called when a 401 error occurs
 * @param action
 */
export const setLogoutAction = (action: () => Promise<void>) => {
  logoutActionHandler = action;
};

// Create the axios instance with shared configuration using the global gateway URL
export const GlobalAxiosClientInstance: AxiosInstance = createIgniteAxiosClientInstance({
  baseURL: GLOBAL_GATEWAY_URL,
  withCredentials: true,
  defaultHeaders: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// Adds correlation ID interceptor to include correlation IDs in all requests and responses for better traceability
// This must be added BEFORE applyIgniteInterceptors so it runs FIRST for errors (response error handlers run in order, not reverse)
const correlationIdInterceptor = createCorrelationIdInterceptor();
GlobalAxiosClientInstance.interceptors.response.use(
  correlationIdInterceptor.onFulfilled,
  correlationIdInterceptor.onRejected,
);

// Apply custom interceptors for authentication, error handling, and logout actions
applyIgniteInterceptors(GlobalAxiosClientInstance, {
  getAuthToken: () => authTokenAccessor && authTokenAccessor(),
  captureException: (message: string | Error, data?: Record<string, unknown>) => captureException(message, data),
  logoutAction: () => {
    if (logoutActionHandler) {
      return logoutActionHandler();
    }
    return Promise.resolve();
  },
});

// Add interceptor to include impersonation header if impersonated user is set in the store
GlobalAxiosClientInstance.interceptors.request.use((config) => {
  const { impersonatedUser } = useBoundStore.getState();
  if (impersonatedUser) {
    config.headers['X-Alt-User-Context'] = impersonatedUser.email;
  }
  return config;
});

export const GlobalAxiosClient = <T>(config: AxiosRequestConfig): Promise<T> => {
  const TIMEOUT =
    parseInt((typeof import.meta !== 'undefined' ? import.meta.env?.VITE_AXIOS_TIMEOUT : undefined) as string, 10) ||
    30000;

  const client = createIgniteAxiosClient<T>({
    instance: GlobalAxiosClientInstance,
    timeout: TIMEOUT,
  });

  return client(config);
};

export type { ErrorType, BodyType };
